import { Component, Input, OnInit } from '@angular/core';
import { PageContainerService, ClientAssetDataService } from '../../../services';
import { Router, NavigationEnd } from '@angular/router';
import { FormControl } from '@angular/forms';
import {
  ClientDto,
  AssetDto,
  GetProjectsByUserCommandRequest,
  GetProjectsByUserCommandResponse,
  ProjectDto
} from '../../../data/CommandServiceDtos.generated';
import { faWrench, faDollyFlatbed, faClipboardList, faLink, faStar, faCircle, faNewspaper, faLifeRing, faExternalLinkAlt, faThLarge, faChartBar, faLayerGroup, faUser } from '@fortawesome/free-solid-svg-icons';

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  clientList: ClientDto[] = [];
  assetList: AssetDto[] = [];
  cachedAssets: AssetDto[] = [];
  clientControl = new FormControl();
  assetControl = new FormControl();
  projectSortControl = new FormControl();
  activeProjectsSelected: string = "active";
  recentProjects: ProjectDto[] = [];
  showFilters: boolean = false;
  faWorkPackIcon = faWrench;
  faMaterialLink = faDollyFlatbed;
  iconExecutionLink = faClipboardList;
  faCXPlannerLink = faLink;
  faStar = faStar;
  faLifeRing = faLifeRing;
  faCircle = faCircle;
  faNewspaper = faNewspaper;
  faExternalLinkAlt = faExternalLinkAlt;
  faThLarge = faThLarge;
  faChartBar = faChartBar;
  faLayerGroup = faLayerGroup;
  hasNewSystemUpdates: boolean = false;
  faUser = faUser;
  @Input() isCollapsed: boolean = false;
  constructor(
    public pcs: PageContainerService,
    public clientOptionsState: ClientAssetDataService,
    private router: Router
  ) {
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    // Emit event to notify app component
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('sidebarToggle', {
        detail: { collapsed: this.isCollapsed }
      }));
    }
  }

  ngOnInit() {
    this.loadRecentProjects();
    this.checkAndLoadData();

    // Listen for login/reset events from session service
    this.pcs.clientSessionService.subscribeToLogin(() => {
      this.checkAndLoadData();
    });

    this.pcs.clientSessionService.subscribeToReset(() => {
      this.clientList = [];
      this.assetList = [];
      this.cachedAssets = [];
    });

    this.assetControl.setValue(0);
    this.clientControl.setValue(0);
    this.projectSortControl.setValue("latest-desc");

    // Listen for updates from other components to refresh sidebar lists
    window.addEventListener('recentProjectsUpdated', () => {
      this.loadRecentProjects();
    });
    window.addEventListener('favoritesUpdated', () => {
      this.loadRecentProjects();
    });

    // Keep sidebar collapse state in sync with the top-nav button toggle
    window.addEventListener('sidebarToggle', (event: any) => {
      if (event && event.detail && typeof event.detail.collapsed === 'boolean') {
        this.isCollapsed = event.detail.collapsed;
      }
    });
  }

  checkAndLoadData() {
    if (this.loggedIn) {
      this.loadData();
      this.checkHasNewSystemUpdates();
    }
  }

  async checkHasNewSystemUpdates() {
    try {
      const response = await this.pcs.apiService.executeCommandService.executeCommand('HasNewSystemUpdates', {}, false, false);
      this.hasNewSystemUpdates = response.Payload.HasNewUpdates === true;
    } catch {
      this.hasNewSystemUpdates = false;
    }
  }

  loadRecentProjects(): void {
    this.recentProjects = JSON.parse(localStorage.getItem('recentProjects') || '[]');
  }

  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
    const years = Math.floor(days / 365);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }

  get loggedIn(): boolean {
    return this.pcs.clientSessionService.loggedIn;
  }

  isActiveRoute(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  checkIfFiltersNeeded() {
    const url = this.router.url;
    this.showFilters = url.includes('/projects') || url === '/dashboard' || url === '/';
  }

  async loadData() {
    const request: GetProjectsByUserCommandRequest = {
      Page: 1,
      PageSize: 1,
      Skip: 0,
      ClientId: 0,
      AssetId: 0,
      ActiveFilter: 'all',
      Search: '',
      Sort: 'latest-desc',
      FavoriteProjectIds: []
    };
    const getCommandResponse: GetProjectsByUserCommandResponse = await this.pcs.apiService.GetProjectsByUser(request);
    this.clientList = getCommandResponse.ClientList;
    this.cachedAssets = getCommandResponse.AssetList;
    this.assetList = this.cachedAssets;

    if (this.clientList.length === 1) {
      this.clientControl.setValue(this.clientList[0]);
      this.clientOptionsState.client = this.clientList[0];
      if (this.cachedAssets.length === 1) {
        this.clientOptionsState.asset = this.cachedAssets[0];
      }
    }
    this.loadState();
  }

  loadState() {
    if (this.clientOptionsState.client && this.clientOptionsState.client.ClientId > 0) {
      this.clientControl.setValue(this.clientOptionsState.client);
      this.filterClients(this.clientOptionsState.client.ClientId);
    }

    if (this.clientOptionsState.asset && this.clientOptionsState.asset.AssetId > 0) {
      this.assetControl.setValue(this.clientOptionsState.asset);
    } else {
      this.assetControl.setValue(0);
    }

    if (this.clientOptionsState.filterOption !== "") {
      this.activeProjectsSelected = this.clientOptionsState.filterOption;
    }

    if (this.clientOptionsState.projectSort !== "") {
      this.projectSortControl.setValue(this.clientOptionsState.projectSort);
    }
  }

  clientSelected() {
    const clientId = this.clientControl.value.ClientId;
    this.projectSortControl.setValue("name-az");
    let client: ClientDto = this.clientControl.value;
    this.clientOptionsState.client = client;
    this.clientOptionsState.projectSort = "name-az";
    this.filterClients(clientId);
    this.emitFilterChange();
  }

  filterClients(clientId: number) {
    if (clientId > 0) {
      this.assetList = this.cachedAssets.filter((item) => item.Client.ClientId === clientId);
      if (this.assetList.length === 1) {
        this.assetControl.setValue(this.assetList[0]);
        this.clientOptionsState.asset = this.assetList[0];
      }
    } else {
      this.assetList = [];
      this.projectSortControl.setValue("client-name-az");
    }
  }

  assetSelected() {
    const assetId = this.assetControl.value.AssetId;
    this.clientOptionsState.asset = this.assetControl.value;
    this.emitFilterChange();
  }

  projectSortChanged() {
    this.clientOptionsState.projectSort = this.projectSortControl.value;
    this.emitFilterChange();
  }

  onProjectTypeChange(value: string) {
    this.clientOptionsState.filterOption = value;
    this.activeProjectsSelected = value;
    this.emitFilterChange();
  }

  emitFilterChange() {
    // Emit event or use service to notify landing page
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('sidebarFilterChange', {
        detail: {
          client: this.clientControl.value,
          asset: this.assetControl.value,
          projectType: this.activeProjectsSelected,
          sort: this.projectSortControl.value
        }
      }));
    }
  }

  compareClients(c1, c2) {
    return c1 && c2 ? c1.ClientId === c2.ClientId : c1 === c2;
  }

  compareAssets(a1, a2) {
    return a1 && a2 ? a1.AssetId === a2.AssetId : a1 === a2;
  }

  onReportsClick() {
    this.pcs.notificationService.warning('Reports', 'Reports are coming soon.', { timeOut: 2500 });
  }

  async openP6() {
    const project = this.recentProjects?.[0];
    if (!project?.ProjectId) {
      this.pcs.notificationService.warning('P6 Dashboard', 'Open a project first to launch P6.', { timeOut: 3000 });
      return;
    }
    try {
      const response = await this.pcs.apiService.GetExecutionStatus({ ProjectId: project.ProjectId }, false, false);
      if (response?.Execution?.ExecutionAccessUrl) {
        window.open(response.Execution.ExecutionAccessUrl.replace(/^http:\/\//i, 'https://'), '_blank');
        return;
      }
    } catch { /* handled below */ }
    this.pcs.notificationService.warning('P6 Dashboard', 'No P6 dashboard is linked for this project.', { timeOut: 3000 });
  }

  async openProPak() {
    const project = this.recentProjects?.[0];
    if (!project?.ProjectId) {
      this.pcs.notificationService.warning('ProPak', 'Open a project first to launch ProPak.', { timeOut: 3000 });
      return;
    }
    try {
      const response = await this.pcs.apiService.GetReadinessSummary(
        { ProjectId: project.ProjectId, ProPakProjectId: project.ProjectId }, false, false);
      const summary = response?.ReadinessSummary;
      const userName = this.pcs.clientSessionService.userName;
      if (summary?.ProPakProjectUrl && userName && summary.PassThroughAuthKey) {
        const base = (summary.ProPakProjectUrl || '').split('#')[0];
        window.open(`${base}#${userName},${summary.PassThroughAuthKey},${project.ProjectId}`, '_blank');
        return;
      }
    } catch { /* handled below */ }
    this.pcs.notificationService.warning('ProPak', 'Unable to open ProPak right now.', { timeOut: 3000 });
  }

  async openSiteSense() {
    const project = this.recentProjects?.[0];
    if (!project?.ProjectId) {
      this.pcs.notificationService.warning('SiteSense', 'Open a project first to launch SiteSense.', { timeOut: 3000 });
      return;
    }
    try {
      const response = await this.pcs.apiService.GetMaterialsStatus({ ProjectId: project.ProjectId }, false, false);
      if (response?.Materials?.MaterialsAccessUrl) {
        window.open(response.Materials.MaterialsAccessUrl, '_blank');
        return;
      }
    } catch { /* handled below */ }
    this.pcs.notificationService.warning('SiteSense', 'No SiteSense link is available for this project.', { timeOut: 3000 });
  }

  openCxPlanner() {
    window.open('https://app.cxplanner.com', '_blank');
  }
}

